import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { useAuthStore } from '@/stores/authStore'
import { settingsPath } from '@/lib/settingsRoutes'
import { useUpgradePrompt, type UpgradeReason } from '@/lib/subscription'

/**
 * The upgrade prompt: opened by any 402 from the API (lib/api.ts →
 * lib/subscription.ts handlePaymentRequired). The server's message says what
 * the plan doesn't cover; the owner gets a way to Settings → Billing,
 * everyone else is told who can change it.
 */

const TITLES: Record<UpgradeReason, string> = {
  read_only: 'Your account is read-only',
  plan_feature: 'Not on your plan',
  seat_limit: 'No seats left',
  automation_limit: 'Automation limit reached',
}

export function UpgradePrompt(): React.ReactElement | null {
  const navigate = useNavigate()
  const { open, code, message, close } = useUpgradePrompt()
  const role = useAuthStore((s) => s.user?.role)
  const isOwner = role === 'admin' || role === 'owner'
  if (!code) return null

  const goToBilling = () => {
    close()
    navigate(settingsPath('billing'))
  }

  return (
    <Modal
      isOpen={open}
      onClose={close}
      title={TITLES[code]}
      size="sm"
      footer={
        <div className="flex flex-wrap justify-end gap-2 w-full">
          <Button variant="secondary" onClick={close}>{isOwner ? 'Not now' : 'OK'}</Button>
          {isOwner && (
            <Button variant="primary" onClick={goToBilling}>
              {code === 'seat_limit' ? 'Add seats' : code === 'read_only' ? 'Pick a plan' : 'See plans'}
            </Button>
          )}
        </div>
      }
    >
      <p style={{ margin: 0, fontSize: 15, lineHeight: 1.5, color: 'var(--midnight-text2)' }}>{message}</p>
      {!isOwner && (
        <p style={{ margin: '12px 0 0', fontSize: 14, lineHeight: 1.5, color: 'var(--midnight-text2)' }}>
          The account owner can change the plan in Settings → Billing.
        </p>
      )}
    </Modal>
  )
}
